import React, {useEffect, useState} from 'react';
import styles from "./recommendations.module.scss";
import {useTranslation} from 'react-i18next';
import {message as messageAnt} from "antd";
import {useAdvisors} from "../../../hooks/advisor.hook";
import {
    Candle,
    IAnalytics, IDiversificationGroupsByTickers,
    IInfoAboutAdviceToAnalyticsPopup, IRatingUser,
    IStrategiesInfo,
    IToOrder, StateOfTrade, TimeFrames,
    TradeAction
} from "../../../shared/types";
import minus from "../../../assets/images/minus.png";
import SellRecomendation from "./sellRecomendation/SellRecomendation";
import {v4 as uuidv4} from "uuid";
import plus from "../../../assets/images/plus.png";
import BuyRecomendation from "./buyRecomendation/BuyRecomendation";
import {CountryExchange} from "../../../shared/countryExchange";
import {useRequest} from "../../../hooks/request.hook";
import PopupAnalytics from "./popupAnalytics/PopupAnalytics";

interface IProps {
    varForUpdate: string,
    strategiesInfo: IStrategiesInfo | undefined,
    countryExchange: CountryExchange | undefined
    membersRatingList: IRatingUser[] | undefined
}

const Recommendations = ({varForUpdate, strategiesInfo, countryExchange, membersRatingList}: IProps) => {

    const {t, i18n} = useTranslation();

    const {loading, getRecommendationsFromAdvisors, getDiversificationGroupsFromAdvisors, getAnalyticsFromAdvisors, getQuotesFromAdvisors, error, clearError} = useAdvisors()
    const {loading: loadingRequest, saveEnteredRequest, error: errorRequest, clearError: clearErrorRequest} = useRequest()

    const [recommendations, setRecommendations] = useState<Record<TradeAction, Partial<Record<string, [string[]]>>>>()//список торговых рекомендаций от советника
    const [btnRequestLoading, setBtnRequestLoading] = useState(false)
    const [analyticsIsShow, setAnalyticsIsShow] = useState(false)//отображается ли попап с аналитикой
    const [analytics, setAnalytics] = useState<IAnalytics>()//объект внешней аналитики для попапа
    const [infoAboutAdviceToAnalyticsPopup, setInfoAboutAdviceToAnalyticsPopup] = useState<IInfoAboutAdviceToAnalyticsPopup>()//объект содержащий информацию об кликнутом совете от стратегии, который передается в попап с аналитикой
    const [quotes, setQuotes] = useState<Record<string, Record<TimeFrames, Candle[]>>>()//графики с сервера
    const [thereIsToAddPosition, setThereIsToAddPosition] = useState<boolean>(false)//есть рекомендации на добавление позиции
    const [diversificationGroupsByTickers, setDiversificationGroupsByTickers] = useState<IDiversificationGroupsByTickers>()

    //функция получения графиков с сервера
    const getQuotes = async () => {
        try {
            const data = await getQuotesFromAdvisors()
            messageAnt.info(data.message)
            return data.info
        } catch (e) {
            messageAnt.info(error);
            clearError()
        }
    }

    //изначальное получение графиков для аналитики
    useEffect(() => {
        if (varForUpdate) {
            const get = async () => {
                const quotes = await getQuotes()
                if (quotes) {
                    setQuotes(quotes)
                }
            }
            get()
        }
    }, [varForUpdate])

    //функция получения групп диверсификации для торговых рекомендаций
    const getDiversificationGroups = async () => {
        try {
            const data = await getDiversificationGroupsFromAdvisors()
            messageAnt.info(data.message)
            return data.info
        } catch (e) {
            messageAnt.info(error)
            clearError()
        }
    }

    //функция получения торговых рекомендаций
    const getRecommendations = async () => {
        try {
            const data = await getRecommendationsFromAdvisors()
            messageAnt.info(data.message)
            return data.info
        } catch (e) {
            messageAnt.info(error);
            clearError()
        }
    }

    //изначальное получение торговых рекомендаций
    useEffect(() => {
        if (varForUpdate) {
            const get = async () => {
                const recommends = await getRecommendations()
                if (recommends) {
                    setRecommendations(recommends)
                }
                const diversificationGroups = await getDiversificationGroups()
                if (diversificationGroups) {
                    setDiversificationGroupsByTickers(diversificationGroups)
                }
            }
            get()
        }
    }, [varForUpdate])

    //проверка есть ли в списке рекомендаций на покупку именно рекомендации на увеличение позиции
    useEffect(() => {
        if (recommendations && recommendations.BUY && Object.keys(recommendations.BUY).length !== 0) {
            let thereIs = false
            Object.keys(recommendations.BUY).forEach((strategy) => {
                recommendations.BUY[strategy]?.forEach((elem) => {
                    if (elem[2] === StateOfTrade.NEED_ADD_ENTER_IN_SERIES) {
                        thereIs = true
                    }
                })
            })
            setThereIsToAddPosition(thereIs)
        }
    }, [recommendations])

    //функция получения аналитики от советника
    const getAnalytics = async () => {
        try {
            const data = await getAnalyticsFromAdvisors()
            messageAnt.info(data.message)
            return data.info
        } catch (e) {
            messageAnt.info(error);
            clearError()
        }
    }

    //изначальное получение аналитики от советника
    useEffect(() => {
        if (varForUpdate) {
            const get = async () => {
                const analytics = await getAnalytics()
                if (analytics) {
                    setAnalytics(analytics)
                }
            }
            get()
        }
    }, [varForUpdate])

    //функция для сохранение данных по выставленной заявке в БД
    const saveRequest = async (country: CountryExchange, requestData: IToOrder) => {
        try {
            const data = await saveEnteredRequest(country, requestData)
            messageAnt.info(data.message)
        } catch (e) {
            messageAnt.info(errorRequest);
            clearErrorRequest()
        }
    }

    //оформление заявок по рекомендации
    const registerRequestHandler = async (request: IToOrder) => {
        setBtnRequestLoading(true)
        if (countryExchange) {
            //сохранение обновленной информации по выставленным заявкам
            await saveRequest(countryExchange, request)
        }
        setBtnRequestLoading(false)
    }

    //всплывающее окно с аналитикой по инструменту, по которому рекомендована покупка
    const showAnalytics = async (request: IInfoAboutAdviceToAnalyticsPopup) => {
        setInfoAboutAdviceToAnalyticsPopup(request)
        setAnalyticsIsShow(true)
    }

    //закрытие окна с аналитикой
    const closeAnalytics = async () => {
        setAnalyticsIsShow(false)
    }

    if (!recommendations || !recommendations.BUY || !recommendations.SELL) {
        return null
    }

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.blockWrap}>
                    <div>
                        {
                            Object.keys(recommendations.SELL).length !== 0 &&
                            <p className={[styles.directionTitle, styles.sell].join(' ')}>
                                <img src={minus} alt=""/>
                                {/*//@ts-ignore*/}
                                {t('AdvisorsToClosePosition')}
                            </p>
                        }
                        {Object.keys(recommendations.SELL).map((strategy) => (
                            <SellRecomendation
                                key={uuidv4()}
                                strategiesInfo={strategiesInfo}
                                recomends={recommendations}
                                strategy={strategy}
                                btnRequestLoading={btnRequestLoading}
                                registerRequestHandler={registerRequestHandler}
                            />
                        ))}
                    </div>
                    <div>
                        {
                            Object.keys(recommendations.BUY).length !== 0 && thereIsToAddPosition &&
                            <p className={[styles.directionTitle, styles.add].join(' ')}>
                                <img src={plus} alt=""/>
                                {/*//@ts-ignore*/}
                                {t('AdvisorsToIncreasePosition')}
                            </p>
                        }
                        {
                            Object.keys(recommendations.BUY).length !== 0 && thereIsToAddPosition &&
                            Object.keys(recommendations.BUY).map((strategy) => (
                                <BuyRecomendation
                                    key={uuidv4()}
                                    strategiesInfo={strategiesInfo}
                                    recomends={recommendations}
                                    strategy={strategy}
                                    btnRequestLoading={btnRequestLoading}
                                    registerRequestHandler={registerRequestHandler}
                                    analytics={analytics}
                                    showAnalytics={showAnalytics}
                                    typeOfBuyRecomendation={StateOfTrade.NEED_ADD_ENTER_IN_SERIES}
                                />
                            ))
                        }
                    </div>
                    <div>
                        {
                            Object.keys(recommendations.BUY).length !== 0 &&
                            <p className={[styles.directionTitle, styles.buy].join(' ')}>
                                <img src={plus} alt=""/>
                                {/*//@ts-ignore*/}
                                {t('AdvisorsToOpenPosition')}
                            </p>
                        }
                        {Object.keys(recommendations.BUY).map((strategy) => (
                            <BuyRecomendation
                                key={uuidv4()}
                                strategiesInfo={strategiesInfo}
                                recomends={recommendations}
                                strategy={strategy}
                                btnRequestLoading={btnRequestLoading}
                                registerRequestHandler={registerRequestHandler}
                                analytics={analytics}
                                showAnalytics={showAnalytics}
                                typeOfBuyRecomendation={StateOfTrade.NEED_OPEN_SERIES}
                                diversificationGroupsByTickers={diversificationGroupsByTickers}
                            />
                        ))}
                    </div>
                </div>
                {
                    infoAboutAdviceToAnalyticsPopup && countryExchange && analytics && quotes && analyticsIsShow && membersRatingList &&
                    <div className={styles.popupAnalyticsWrap}>
                        <PopupAnalytics closeHandler={closeAnalytics}
                                        infoAboutAdvice={infoAboutAdviceToAnalyticsPopup}
                                        analytics={analytics}
                                        membersRating={membersRatingList}
                                        quotes={quotes}/>
                    </div>
                }
            </div>
        </div>
    );
};

export default Recommendations;
